// frontend/src/pages/TermsOfServicePage.js
import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, CheckCircle, ArrowLeft, Mail, Phone, MapPin, AlertCircle, Shield, Store, Users } from 'lucide-react';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 shadow-sm">
        <div className="content-container px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <img 
                src="/fastel-logo.png" 
                alt="Fastel" 
                className="h-8 w-auto"
              />
              <span className="text-xl font-bold bg-gradient-to-r from-primary-700 via-primary-600 to-primary-400 bg-clip-text text-transparent">
                Fastel
              </span>
            </div>
            <Link to="/" className="text-sm text-gray-600 hover:text-primary-600 flex items-center">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Terms of Service Content */}
      <div className="content-container px-4 sm:px-6 lg:px-8 py-12 max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-primary-50 rounded-lg">
              <FileText className="h-6 w-6 text-primary-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800">Terms of Service</h1>
          </div>
          
          <p className="text-sm text-gray-500 mb-6">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <div className="space-y-6 text-sm text-gray-600">
            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">1. Acceptance of Terms</h2>
              <p>
                By downloading, installing, or using the Fastel mobile application and website ("Service"), 
                you agree to be bound by these Terms of Service. If you do not agree to these terms, 
                please do not use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">2. User Accounts</h2>
              <div className="space-y-2">
                <p><strong>Eligibility:</strong> Anyone can create an account on Fastel. There is no age restriction, but you must provide accurate information.</p>
                <p><strong>Account Responsibility:</strong> You are responsible for all activity that occurs under your account. Keep your login credentials secure.</p>
                <p><strong>Multiple Accounts:</strong> Users are not permitted to create multiple accounts. Each person may have only one account.</p>
                <p><strong>Inactive Accounts:</strong> Inactive accounts are not automatically deleted. You may request account deletion through our support team.</p>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">3. User Conduct</h2>
              <p>You agree not to use the Service to:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Post spam, fraudulent, or misleading content</li>
                <li>Harass, abuse, or threaten other users</li>
                <li>Post illegal, obscene, or harmful content</li>
                <li>Impersonate any person or entity</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Attempt to gain unauthorized access to the Service</li>
                <li>Interfere with or disrupt the Service</li>
              </ul>
            </section>

            {/* ========== UPDATED: Marketplace & Business Listings ========== */}
            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                <Store className="h-5 w-5 text-primary-600 mr-2" />
                4. Advertising Platform – Important
              </h2>
              
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg mb-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Important Notice</p>
                    <p className="text-sm text-amber-700">
                      Fastel is an <strong>advertising and business-discovery platform only</strong>. 
                      We do not facilitate, process, or guarantee transactions between users.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <p><strong className="text-gray-800">What Fastel Is:</strong></p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>An advertising platform where businesses can showcase their products and services</li>
                  <li>A discovery tool for users to find businesses and services</li>
                  <li>A networking platform to connect entrepreneurs and business owners</li>
                </ul>

                <p className="mt-3"><strong className="text-gray-800">What Fastel Is NOT:</strong></p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>A marketplace for buying and selling goods or services</li>
                  <li>An escrow service or payment processor between users</li>
                  <li>A guarantor of any transaction between users and advertisers</li>
                  <li>A seller of any products or services advertised on the platform</li>
                </ul>

                <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-800 flex items-start">
                    <Shield className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong>Fastel does not provide an in-app checkout system, escrow service, 
                      payment processing, delivery service, or transaction guarantee.</strong> 
                      Users and advertisers interact entirely at their own discretion and risk.
                    </span>
                  </p>
                </div>
              </div>
            </section>

            {/* ========== NEW: User Responsibility & Fraud Prevention ========== */}
            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                <Shield className="h-5 w-5 text-primary-600 mr-2" />
                5. User Responsibility & Fraud Prevention
              </h2>
              
              <div className="space-y-3">
                <p>
                  Users are solely responsible for exercising appropriate caution and conducting 
                  their own due diligence before dealing with any person or business discovered through Fastel.
                </p>

                <p className="mt-2">
                  <strong>Fastel strongly advises users to:</strong>
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Independently verify the identity, legitimacy, and reputation of any advertiser</li>
                  <li>Verify product quality, service claims, prices, and business ownership before engaging</li>
                  <li>Never share sensitive financial information, passwords, PINs, or OTPs</li>
                  <li>Be cautious of deals that seem too good to be true</li>
                  <li>Avoid moving transactions to unverified external platforms</li>
                </ul>

                <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-800 flex items-start">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong>Warning Signs to Watch For:</strong> Requests for payment before delivery, 
                      pressure to make immediate payments, requests for sensitive personal information, 
                      unrealistic prices or returns, and refusal to provide verifiable information.
                    </span>
                  </p>
                </div>

                <p className="mt-2">
                  <strong>Fastel does not guarantee that every advertiser is genuine, legitimate, 
                  trustworthy, or free from fraudulent activity.</strong> 
                  Although Fastel may take reasonable measures to moderate advertisements, we cannot 
                  guarantee that every fraudulent, misleading, or deceptive advertisement will be 
                  detected or removed before users encounter it.
                </p>
              </div>
            </section>

            {/* ========== NEW: No Responsibility for User Dealings ========== */}
            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                <Users className="h-5 w-5 text-primary-600 mr-2" />
                6. No Responsibility for User-to-User Dealings
              </h2>
              
              <div className="space-y-3">
                <p>
                  Any communication, negotiation, agreement, payment, service arrangement, business 
                  relationship, or other interaction between a user and an advertiser is conducted 
                  solely between those parties.
                </p>

                <p>
                  <strong>Fastel is not a party to such arrangements</strong> and does not act as an 
                  agent, broker, intermediary, guarantor, escrow provider, seller, buyer, or 
                  representative of either party.
                </p>

                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-700">
                    <strong>To the maximum extent permitted by applicable law, Fastel shall not be 
                    responsible or liable for:</strong>
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-gray-600">
                    <li>Fraud, scams, or misrepresentation by advertisers</li>
                    <li>Defective, undelivered, or misrepresented products or services</li>
                    <li>Non-payment, financial loss, or damage</li>
                    <li>Personal injury, property damage, or disputes</li>
                    <li>Any other harm arising from dealings between users and advertisers</li>
                  </ul>
                </div>

                <p className="mt-2">
                  <strong>Users interact with advertisers at their own discretion and risk.</strong>
                </p>
              </div>
            </section>

            {/* ========== UPDATED: Reporting & Moderation ========== */}
            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">7. Reporting & Moderation</h2>
              <div className="space-y-2">
                <p><strong>Reporting:</strong> Users can report inappropriate content, suspicious advertisers, fraudulent advertisements, or users through the app's reporting feature.</p>
                <p><strong>Review Process:</strong> All reports are reviewed manually by our team.</p>
                <p><strong>Actions:</strong> Depending on the severity, violations may result in warnings, temporary suspension, permanent banning, or removal of advertisements.</p>
                <p><strong>Fastel reserves the right to:</strong></p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Remove advertisements at its discretion</li>
                  <li>Suspend or terminate accounts without prior notice</li>
                  <li>Take appropriate action against suspected fraud, deception, illegal activity, or repeated violations</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">8. Transactions & Payments</h2>
              <div className="space-y-2">
                <p><strong>Wallet:</strong> Users can fund their wallets using third-party payment processors (Monnify, Paystack).</p>
                <p><strong>Failed Transactions:</strong> If a transaction fails for any reason, the amount will be refunded to your wallet.</p>
                <p><strong>Refund Policy:</strong> Transactions are generally final. Refunds are issued only for failed transactions or at our discretion.</p>
                <p><strong>Third-Party Providers:</strong> Fastel uses third-party providers for VTU and bill payments. We are not liable for provider failures, outages, or errors.</p>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">9. Content Ownership & License</h2>
              <div className="space-y-2">
                <p><strong>User Content:</strong> You retain all rights to the content you post on Fastel (posts, business listings, photos, etc.).</p>
                <p><strong>License to Fastel:</strong> By posting content, you grant Fastel a license to display, distribute, and promote your content on our platform and for promotional purposes.</p>
                <p><strong>Content Removal:</strong> We reserve the right to remove any content that violates our terms or is reported by other users.</p>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">10. Dispute Resolution</h2>
              <div className="space-y-2">
                <p><strong>User Disputes:</strong> If there is a dispute between users, our team will investigate.</p>
                <p><strong>Resolution Process:</strong> We may issue warnings, temporary suspensions, or permanent bans depending on the severity of the violation.</p>
                <p><strong>Final Decision:</strong> Fastel's decision on disputes is final and binding.</p>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">11. Termination</h2>
              <div className="space-y-2">
                <p><strong>By You:</strong> You may stop using our Service at any time. Account deletion can be requested through our support team.</p>
                <p><strong>By Us:</strong> We may suspend or terminate your account for violations of these terms, without prior notice.</p>
                <p><strong>Reasons:</strong> Termination may occur for illegal activity, harassment, fraud, spam, or any conduct we deem harmful to our community.</p>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">12. Disclaimer of Warranties</h2>
              <div className="space-y-2">
                <p>The Service is provided "as is" and "as available" without warranties of any kind.</p>
                <p><strong>No Liability:</strong> Fastel is not liable for:</p>
                <ul className="list-disc pl-5 mt-1 space-y-1">
                  <li>Inaccuracies in business listings</li>
                  <li>Service outages or interruptions</li>
                  <li>VTU provider failures</li>
                  <li>Third-party payment processor issues</li>
                  <li>User interactions or disputes</li>
                  <li>Fraud, scams, or misrepresentation by advertisers</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">13. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the Federal Republic of Nigeria.
                Any legal disputes arising from these terms shall be resolved in the courts of Kano State, Nigeria.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">14. Changes to Terms</h2>
              <p>
                We reserve the right to update these Terms of Service at any time. We will notify users of 
                significant changes through the app or via email. Continued use of the Service constitutes 
                acceptance of the updated terms.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">15. Contact Us</h2>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-primary-600" />
                  <span><strong>Email:</strong> fasteltelecomltd@gmail.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-primary-600" />
                  <span><strong>Phone:</strong> 08109650254, 09042183619</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4 text-primary-600" />
                  <span><strong>Address:</strong> NO. 35, MIJINYAWA STREET TUDUN JUKUN, ZARIA, KADUNA STATE, NIGERIA</span>
                </div>
              </div>
            </section>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200 text-center text-xs text-gray-400">
            <p>&copy; {new Date().getFullYear()} Fastel Telecom LTD. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;